import React, { useEffect, useState } from "react";
import {
  confirmDailyIssue,
  fetchDailyIssue,
  fetchIngredients,
  getRole,
  saveDailyIssue,
} from "../services/api";
import { useT } from "../i18n/useT";

function todayLocal() {
  return new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
}

// F&B Cost Control — morning kitchen issue (requisition).
// Controller/Manager registers the ingredients issued to the kitchen for the
// day; Chef confirms receipt and may adjust each line to what actually arrived.
export default function KitchenIssuePage() {
  const { t } = useT();
  const role = getRole();
  const canRegister = role === "manager" || role === "controller";

  const [date, setDate] = useState(todayLocal());
  const [ings, setIngs] = useState([]);
  const [issue, setIssue] = useState(null);      // server payload for the day
  const [issued, setIssued] = useState({});      // ingredient_id -> issued (string)
  const [received, setReceived] = useState({});  // ingredient_id -> received (string)
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canRegister) return;
    fetchIngredients().then(setIngs).catch(() => {});
  }, [canRegister]);

  const load = () => {
    setLoading(true); setErr(""); setMsg("");
    fetchDailyIssue(date)
      .then((d) => {
        setIssue(d);
        const iss = {}, rec = {};
        (d.lines || []).forEach((l) => {
          iss[l.ingredient_id] = String(l.issued_qty);
          if (l.received_qty != null) rec[l.ingredient_id] = String(l.received_qty);
        });
        setIssued(iss); setReceived(rec);
      })
      .catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [date]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveIssue = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const lines = ings
        .filter((i) => issued[i.id] !== undefined && issued[i.id] !== "" && Number(issued[i.id]) > 0)
        .map((i) => ({ ingredient_id: i.id, issued_qty: Number(issued[i.id]) || 0 }));
      const d = await saveDailyIssue({ issue_date: date, lines });
      setIssue(d); setReceived({});
      setMsg(t("cc_issue_saved"));
    } catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_save")); }
    finally { setSaving(false); }
  };

  const confirmIssue = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const lines = (issue.lines || []).map((l) => ({
        ingredient_id: l.ingredient_id,
        received_qty: received[l.ingredient_id] !== undefined && received[l.ingredient_id] !== ""
          ? Number(received[l.ingredient_id]) || 0
          : l.issued_qty,
      }));
      const d = await confirmDailyIssue({ issue_date: date, lines });
      setIssue(d);
      const rec = {};
      (d.lines || []).forEach((l) => { if (l.received_qty != null) rec[l.ingredient_id] = String(l.received_qty); });
      setReceived(rec);
      setMsg(t("cc_issue_confirmed_msg"));
    } catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_save")); }
    finally { setSaving(false); }
  };

  const status = issue && issue.exists ? issue.status : null;
  const statusLabel = status === "confirmed" ? t("cc_issue_status_confirmed")
    : status === "issued" ? t("cc_issue_status_issued")
    : t("cc_issue_status_none");
  const statusColor = status === "confirmed" ? "#1D9E75" : status === "issued" ? "#854F0B" : "#5f6b78";

  return (
    <>
      <h1>📦 {t("cc_nav_issue")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 640 }}>
        {canRegister ? t("cc_issue_intro_ctrl") : t("cc_issue_intro_chef")}
      </p>

      <div className="card" style={{ marginTop: 12, maxWidth: 640 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_day")}<br />
            <input type="date" value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <span style={{ fontWeight: 600, fontSize: 13, color: statusColor }}>● {statusLabel}
            {status === "confirmed" && issue.confirmed_at
              ? <span className="muted" style={{ fontWeight: 400 }}> · {new Date(issue.confirmed_at).toLocaleString()}</span>
              : null}
          </span>
        </div>

        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

        {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : (
          <div style={{ marginTop: 14 }}>
            {/* ---------- Controller / Manager: register the issue ---------- */}
            {canRegister ? (
              ings.length === 0 ? <p className="muted">{t("cc_no_ings_defined")}</p> : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 10px", alignItems: "center" }}>
                    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>{t("cc_ingredient")}</div>
                    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "right" }}>{t("cc_issued")}</div>
                    {ings.map((i) => {
                      const line = (issue?.lines || []).find((l) => l.ingredient_id === i.id);
                      const rcv = line && line.received_qty != null ? line.received_qty : null;
                      const mismatch = rcv != null && Number(rcv) !== Number(line.issued_qty);
                      return (
                        <React.Fragment key={i.id}>
                          <span>{i.name}
                            {status === "confirmed" && rcv != null && (
                              <span style={{ fontSize: 12, marginLeft: 8, color: mismatch ? "#A32D2D" : "#1D9E75" }}>
                                ({t("cc_received")}: {rcv} {i.unit}{mismatch ? ` · ${t("cc_issue_mismatch")}` : ""})
                              </span>
                            )}
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <input type="number" min="0" step="0.001" value={issued[i.id] ?? ""}
                              onChange={(e) => setIssued({ ...issued, [i.id]: e.target.value })}
                              placeholder="0" style={{ width: 100, textAlign: "right" }} />
                            <span className="muted" style={{ width: 34, fontSize: 13 }}>{i.unit}</span>
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 16 }}>
                    <button className="btn" onClick={saveIssue} disabled={saving}>{saving ? t("cc_saving") : t("cc_issue_save")}</button>
                    {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
                  </div>
                </>
              )
            ) : (
              /* ---------- Chef: confirm receipt ---------- */
              !issue || !issue.exists || (issue.lines || []).length === 0 ? (
                <p className="muted">{t("cc_issue_none_for_day")}</p>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "6px 12px", alignItems: "center" }}>
                    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>{t("cc_ingredient")}</div>
                    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "right" }}>{t("cc_issued")}</div>
                    <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "right" }}>{t("cc_received")}</div>
                    {(issue.lines || []).map((l) => {
                      const val = received[l.ingredient_id] ?? String(l.issued_qty);
                      const mismatch = Number(val) !== Number(l.issued_qty);
                      return (
                        <React.Fragment key={l.ingredient_id}>
                          <span>{l.name}</span>
                          <span style={{ textAlign: "right" }}>{l.issued_qty} <span className="muted" style={{ fontSize: 12 }}>{l.unit}</span></span>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <input type="number" min="0" step="0.001" value={received[l.ingredient_id] ?? l.issued_qty}
                              onChange={(e) => setReceived({ ...received, [l.ingredient_id]: e.target.value })}
                              style={{ width: 100, textAlign: "right", color: mismatch ? "#A32D2D" : undefined, fontWeight: mismatch ? 600 : 400 }} />
                            <span className="muted" style={{ width: 34, fontSize: 13 }}>{l.unit}</span>
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 16 }}>
                    <button className="btn" onClick={confirmIssue} disabled={saving}>{saving ? t("cc_saving") : t("cc_issue_confirm")}</button>
                    {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
                  </div>
                  <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>{t("cc_issue_confirm_hint")}</p>
                </>
              )
            )}
          </div>
        )}
      </div>
    </>
  );
}
